import React from 'react';
import { useNavigate } from 'react-router-dom';
import PrerenderReady from './PrerenderReady';
import { useLang } from '../contexts/LangContext';
import PublicToggleBar from './PublicToggleBar';

// 09.08: kolorystyka tego pliku uzywa TYCH SAMYCH tokenow Semafor co landing page (.galp w
// GapAssessmentLandingPage.jsx) - wersja jasna, jedyna wersja platformy widoczna publicznie
// (bez logowania). Zdublowane tu (nie import), bo landing page swiadomie nie dzieli namespace'u
// klas z .ga-root/apka - patrz komentarz na gorze tamtego pliku.
// 11.08: ciemny motyw dodany do tego samego wbudowanego <style> (patrz CennikPage.jsx dla pelnego
// kontekstu wzorca) - .legalp nie deklaruje --red/--red-soft/--panelA/--panelB, wiec ten blok ich
// tez nie emituje.
const LEGAL_TOKENS = `
.legalp{
  --paper:#E7E8E3; --paper-raised:#F8F9F6; --panel2:#DEE1DA; --ink:#1B2124; --ink-soft:#5C6569;
  --line:#C6CAC2; --line-soft:#D9DCD5;
  --accent:#1E7A46; --accent-ink:#FFFFFF; --accent-soft:#DCEEE2;
  --amber:#9C6A16; --amber-soft:#F3E4C8;
  --shadow:0 1px 2px rgba(20,30,25,.05), 0 8px 20px -12px rgba(20,30,25,.14);
}
[data-theme='dark'] .legalp{
  --paper:#15181A; --paper-raised:#1E2225; --panel2:#262B27; --ink:#E9EAE4; --ink-soft:#8B948F;
  --line:#33383A; --line-soft:#282C2E;
  --accent:#3CB878; --accent-ink:#0B1712; --accent-soft:#1B2E22;
  --amber:#D79A3C; --amber-soft:#332A18;
  --shadow:0 1px 2px rgba(0,0,0,.4), 0 10px 24px -12px rgba(0,0,0,.55);
}
`;

const UPDATED = '2026-08-09';

const SECTIONS = [
  {
    id: 'administrator',
    title: '1. Administrator danych osobowych',
    content: `Administratorem danych osobowych użytkowników serwisu Gap Assessment by MQS (dostępnego pod adresem gapassessment.pl) jest:

**MQS MARCIN RABIASZ**
NIP: 8971742369
ul. Hiacyntów 5, 41-600 Świętochłowice (woj. śląskie)
Kontakt: kontakt@mqs.com.pl

W sprawach dotyczących przetwarzania danych osobowych i realizacji praw przysługujących na podstawie RODO prosimy o kontakt pod powyższy adres e-mail.`,
  },
  {
    id: 'dane',
    title: '2. Jakie dane zbieramy i skąd pochodzą',
    content: `W zależności od sposobu korzystania z serwisu przetwarzamy następujące kategorie danych:

**2.1 Dane konta użytkownika**
- Adres e-mail (podawany przy rejestracji lub logowaniu)
- Zaszyfrowane hasło (przechowywane jako hash bcrypt - nigdy w postaci plaintext)
- Imię i nazwisko (opcjonalnie, wyświetlane w panelu i na liście zespołu)
- Data i godzina rejestracji, data ostatniego logowania

**2.2 Dane przestrzeni klienta (portfel)**
Serwis jest narzędziem dla konsultantów i audytorów ISO zarządzających portfelem klientów. Tworząc przestrzeń dla każdego klienta użytkownik podaje:
- Nazwę firmy klienta
- Branżę / profil działalności
- Wybraną normę lub regulację, względem której prowadzona jest analiza

**2.3 Pliki wgrywane do analizy Gap Audit**
Treść dokumentów DOCX/PDF/XLSX wgranych do analizy (w formie tekstowej, wyekstrahowanej z pliku) jest zapisywana razem z wynikiem analizy - wynik i treść źródłowa są przechowywane przez okres skonfigurowany w ustawieniach konta (patrz sekcja 4), nie są usuwane natychmiast po zakończeniu sesji. Jeśli użytkownik włączył opcję anonimizacji nazwy firmy, do przetwarzania i do API dostawcy AI trafia tekst z zanonimizowaną nazwą.

**2.4 Propozycje poprawek generowane przez AI**
Gdy użytkownik akceptuje propozycję poprawki (pojedynczej lub w trybie wsadowym), treść poprawki jest zapisywana wprost w pliku .docx wyniku analizy - staje się częścią tego samego dokumentu i podlega tym samym zasadom przechowywania.

**2.5 Dane zespołu**
Właściciel konta może zaprosić współpracowników (audytorów, zatwierdzających) do wspólnej pracy nad portfelem. Zapraszając członka zespołu przetwarzamy jego adres e-mail, przypisaną rolę (Właściciel / Audytor / Zatwierdzający) oraz - jeśli dotyczy - zawężenie dostępu do jednej, konkretnej przestrzeni klienta.

**2.6 Publiczny link do raportu**
Użytkownik z rolą Właściciela lub Zatwierdzającego może wygenerować bezpieczny, publiczny link do zatwierdzonego wyniku analizy (np. w celu przekazania go ocenianej organizacji). Link zawiera losowy token dostępowy, jest ważny przez okres skonfigurowany w ustawieniach konta i może zostać unieważniony w dowolnym momencie. Osoby posiadające link widzą treść raportu bez konieczności logowania - decyzja o wygenerowaniu i udostępnieniu linku należy wyłącznie do użytkownika.

**2.7 Dane techniczne i logi serwera**
- Adres IP
- Typ i wersja przeglądarki (User-Agent)
- Data i godzina żądania HTTP, adres URL wywołania, kod odpowiedzi HTTP

**2.8 Statystyki odwiedzin**
Serwis liczy odwiedziny stron publicznych (adres, ewentualne parametry UTM, zewnętrzne źródło odesłania przy pierwszym wejściu) na potrzeby własnych, prostych statystyk ruchu. Identyfikator wizyty przechowywany jest wyłącznie w sessionStorage przeglądarki (znika po zamknięciu karty) - nie jest to plik cookie i nie służy do rozpoznawania powracającego odwiedzającego pomiędzy wizytami. Szczegóły w sekcji 6.`,
  },
  {
    id: 'cele',
    title: '3. Cele i podstawy prawne przetwarzania',
    content: `| Cel przetwarzania | Podstawa prawna (RODO) |
|---|---|
| Świadczenie usług serwisu (konto, analiza Gap Audit, zarządzanie portfelem klientów) | Art. 6 ust. 1 lit. b - wykonanie umowy |
| Obsługa zapytań biznesowych i umówienie prezentacji produktu | Art. 6 ust. 1 lit. b - działania na żądanie osoby, której dane dotyczą, przed zawarciem umowy |
| Zapewnienie bezpieczeństwa serwisu i wykrywanie nadużyć | Art. 6 ust. 1 lit. f - prawnie uzasadniony interes |
| Proste statystyki ruchu na stronach publicznych (bez plików cookie, patrz sekcja 6) | Art. 6 ust. 1 lit. f - prawnie uzasadniony interes |
| Wypełnianie obowiązków prawnych (np. księgowość) | Art. 6 ust. 1 lit. c - obowiązek prawny |
| Dochodzenie roszczeń lub obrona przed roszczeniami | Art. 6 ust. 1 lit. f - prawnie uzasadniony interes |

**Prawnie uzasadniony interes administratora** polega na zapewnieniu bezpieczeństwa i stabilności serwisu oraz możliwości dochodzenia roszczeń wynikających ze świadczonych usług.`,
  },
  {
    id: 'czas',
    title: '4. Czas przechowywania danych',
    content: `| Kategoria danych | Czas przechowywania |
|---|---|
| Dane konta użytkownika | Do usunięcia konta lub przez 3 lata od ostatniej aktywności |
| Wyniki Gap Audit i treść wgranych plików źródłowych | Domyślnie **24 miesiące** od daty analizy - właściciel konta może skrócić lub wydłużyć ten okres w Ustawieniach. Po upływie terminu dokumenty i powiązane pliki są automatycznie i trwale usuwane (proces cykliczny, uruchamiany raz na dobę) |
| Token publicznego linku do raportu | Według okresu skonfigurowanego w Ustawieniach konta (domyślnie 14 dni) od wygenerowania linku, niezależnie od terminu przechowywania samego dokumentu - link można też unieważnić ręcznie w dowolnym momencie |
| Dane zaproszonego członka zespołu | Do usunięcia z zespołu przez właściciela konta |
| Logi serwera | 30 dni, po czym automatycznie usuwane |
| Identyfikator wizyty (sessionStorage, statystyki ruchu) | Do zamknięcia karty przeglądarki - nie jest trwale zapisywany |

Użytkownik może w dowolnym momencie samodzielnie usunąć dokumenty klienta w zakładce Historia danej przestrzeni klienta, niezależnie od skonfigurowanego okresu przechowywania. Po upływie wskazanych terminów dane są trwale usuwane w sposób uniemożliwiający identyfikację osoby lub organizacji.`,
  },
  {
    id: 'prawa',
    title: '5. Prawa przysługujące użytkownikowi',
    content: `Na podstawie RODO (Rozp. 2016/679) przysługują Ci następujące prawa:

**Prawo dostępu** (art. 15) - możesz zażądać informacji o tym, jakie Twoje dane przetwarzamy, w jakim celu i na jakiej podstawie.

**Prawo do sprostowania** (art. 16) - możesz zażądać poprawienia nieprawdziwych lub uzupełnienia niekompletnych danych.

**Prawo do usunięcia** (art. 17) - możesz zażądać usunięcia danych ("prawo do bycia zapomnianym") w przypadkach przewidzianych przez prawo.

**Prawo do ograniczenia przetwarzania** (art. 18) - możesz zażądać czasowego wstrzymania przetwarzania Twoich danych w określonych sytuacjach.

**Prawo do przenośności danych** (art. 20) - możesz otrzymać Twoje dane w formacie nadającym się do odczytu maszynowego.

**Prawo sprzeciwu** (art. 21) - możesz sprzeciwić się przetwarzaniu danych opartemu na prawnie uzasadnionym interesie.

**Prawo do skargi** - masz prawo wnieść skargę do organu nadzorczego: Urząd Ochrony Danych Osobowych (UODO), ul. Stawki 2, 00-193 Warszawa, www.uodo.gov.pl.

Aby skorzystać z powyższych praw, skontaktuj się z nami pod adresem: kontakt@mqs.com.pl. Odpowiemy w ciągu 30 dni od otrzymania wniosku.`,
  },
  {
    id: 'cookies',
    title: '6. Pliki cookies i lokalne przechowywanie danych',
    content: `**Gap Assessment by MQS nie używa plików cookies.** Ani do logowania, ani do śledzenia, ani do celów marketingowych czy analitycznych trzeciej strony (brak Google Analytics, Google Tag Managera i podobnych narzędzi).

**Co przechowujemy zamiast cookies:**

| Nazwa / mechanizm | Technologia | Cel | Czas życia |
|---|---|---|---|
| gap_mvp_token | localStorage | Token sesji zalogowanego użytkownika (autoryzacja zapytań do API) | Do wylogowania lub ręcznego wyczyszczenia danych przeglądarki |
| iso_traffic_session | sessionStorage | Identyfikator pojedynczej wizyty na potrzeby prostych statystyk ruchu stron publicznych (sekcja 2.8) | Do zamknięcia karty przeglądarki |

Obie technologie są **ściśle niezbędne** do działania serwisu (utrzymanie zalogowanej sesji) lub mają charakter tymczasowy i nie służą do budowania profilu odwiedzającego - nie wymagają zgody w rozumieniu przepisów o cookies (art. 173 Prawa telekomunikacyjnego), ponieważ nie są plikami cookies i nie są używane do śledzenia.

Wyłączenie localStorage/sessionStorage w przeglądarce uniemożliwi zalogowanie się do serwisu.`,
  },
  {
    id: 'trzecie',
    title: '7. Odbiorcy danych - podmioty trzecie',
    content: `Twoje dane mogą być przekazywane następującym kategoriom podmiotów:

**7.1 Dostawcy infrastruktury IT**
- **Hostinger International Ltd.** - hosting serwera VPS (serwery w Niemczech / UE). Dane są przetwarzane na terytorium Unii Europejskiej.

**7.2 Dostawcy AI (sztucznej inteligencji)**
Do analizy Gap Audit i generowania propozycji poprawek system korzysta z jednego z kilku dostawców modeli językowych, w zależności od dostępności - dane wgrane przez użytkownika (nazwa klienta, branża, treść wgranych dokumentów) są przekazywane do aktualnie używanego dostawcy w celu realizacji usługi:
- **Mistral AI** (Francja, UE) - podstawowy dostawca. Polityka prywatności: mistral.ai/terms/.
- **DeepSeek** (Chiny) - dostawca zapasowy. Polityka prywatności: deepseek.com/privacy-policy.
- **Groq, Inc.** (USA) - dostawca zapasowy (infrastruktura inferencji). Polityka prywatności: groq.com/privacy-policy/.

Treść dokumentu przekazywana do dostawcy AI jest zawsze traktowana wyłącznie jako dane do analizy - system stosuje dodatkowe zabezpieczenia programowe ograniczające ryzyko, że polecenia ukryte w treści wgranego dokumentu wpłyną na działanie modelu. W przypadku dostawców spoza UE (DeepSeek, Groq) stosowane są odpowiednie zabezpieczenia zgodne z SCCs (Standardowe Klauzule Umowne) wg art. 46 RODO, w zakresie w jakim dany dostawca je oferuje.

**7.3 Organy publiczne**
Administrator może być zobowiązany do udostępnienia danych organom publicznym (np. policja, prokuratura, sądy) na podstawie przepisów prawa.

Nie sprzedajemy, nie wynajmujemy ani nie udostępniamy danych osobowych podmiotom trzecim w celach marketingowych.

**7.4 Dane osób trzecich wprowadzane przez użytkownika**
Serwis jest narzędziem dla konsultantów i audytorów ISO, którzy w ramach swojej pracy wgrywają do analizy Gap Audit dane dotyczące WŁASNYCH klientów - w tym nazwy firm i treść ich wewnętrznych dokumentów. W takim zakresie to użytkownik jest administratorem tych danych, a Administrator serwisu (MQS Marcin Rabiasz) działa jako podmiot przetwarzający w rozumieniu art. 28 RODO. Użytkownik odpowiada za posiadanie własnej podstawy prawnej przetwarzania danych swoich klientów. Serwis oferuje opcjonalną funkcję anonimizacji nazwy firmy przed wysłaniem danych do dostawcy AI (patrz sekcja 2.3) - jej włączenie leży w gestii użytkownika. Umowa powierzenia przetwarzania danych (art. 28 RODO) jest dostępna na życzenie - kontakt: kontakt@mqs.com.pl.

**7.5 Odbiorcy publicznego linku do raportu**
Jeśli użytkownik świadomie wygeneruje i udostępni publiczny link do wyniku analizy (sekcja 2.6), każda osoba posiadająca ten link (np. przedstawiciel ocenianej organizacji) uzyska dostęp do treści raportu bez logowania. To świadoma decyzja użytkownika, nie automatyczne udostępnienie przez Administratora - link można w każdej chwili unieważnić.

**7.6 Dostawca płatności**
Przy zakupie planu Starter lub Growth dane niezbędne do realizacji płatności (m.in. adres e-mail, kwota transakcji, adres IP) są przekazywane do **PayU S.A.** (ul. Grunwaldzka 186, 60-166 Poznań, KRS 0000274399) - krajowej instytucji płatniczej nadzorowanej przez Komisję Nadzoru Finansowego, działającej jako niezależny administrator tych danych w zakresie realizacji płatności. Administrator nie przechowuje danych karty płatniczej ani danych logowania do bankowości użytkownika. Polityka prywatności PayU: payu.pl.`,
  },
  {
    id: 'bezpieczenstwo',
    title: '8. Bezpieczeństwo danych',
    content: `Stosujemy następujące środki techniczne i organizacyjne w celu ochrony danych:

- Szyfrowanie połączeń protokołem TLS/HTTPS (certyfikat SSL)
- Hasła przechowywane wyłącznie jako hash (bcrypt), stało-czasowa weryfikacja logowania odporna na wyciek informacji o istnieniu konta
- Weryfikacja rzeczywistej sygnatury (magic bytes) każdego wgrywanego pliku - nie tylko deklarowanego rozszerzenia czy typu MIME
- Limit rozmiaru treści po rozpakowaniu wgranych plików .docx/.xlsx, chroniący przed nadmiernym zużyciem pamięci serwera
- Dostęp do serwera ograniczony kluczami SSH
- Izolacja danych pomiędzy kontami i przestrzeniami klientów, wymuszana po stronie serwera przy każdym żądaniu
- Regularny backup danych, zasada minimalnych uprawnień przy dostępie do danych

Mimo stosowanych zabezpieczeń, żadna transmisja danych przez internet nie jest w 100% bezpieczna. W przypadku naruszenia ochrony danych osobowych poinformujemy użytkowników i UODO zgodnie z wymogami RODO (art. 33-34).`,
  },
  {
    id: 'zmiany',
    title: '9. Zmiany polityki prywatności',
    content: `Administrator zastrzega sobie prawo do wprowadzania zmian w niniejszej Polityce Prywatności.

O istotnych zmianach będą informowani użytkownicy posiadający konto w serwisie (drogą e-mail) oraz wszyscy odwiedzający stronę (przez komunikat na stronie głównej).

Data ostatniej aktualizacji: **${UPDATED}**

Aktualna wersja Polityki Prywatności jest zawsze dostępna pod adresem: gapassessment.pl/polityka-prywatnosci`,
  },
  {
    id: 'kontakt',
    title: '10. Kontakt',
    content: `W sprawach dotyczących ochrony danych osobowych prosimy o kontakt:

**Email:** kontakt@mqs.com.pl
**Strona:** gapassessment.pl

**Organ nadzorczy:**
Urząd Ochrony Danych Osobowych (UODO)
ul. Stawki 2, 00-193 Warszawa
Tel.: 606-950-000
www.uodo.gov.pl`,
  },
];

const SECTIONS_EN = [
  {
    id: 'administrator',
    title: '1. Data Controller',
    content: `The data controller for users of the Gap Assessment by MQS service (available at gapassessment.pl) is:

**MQS MARCIN RABIASZ**
Tax ID (NIP): 8971742369
ul. Hiacyntow 5, 41-600 Swietochlowice (Silesia, Poland)
Contact: kontakt@mqs.com.pl

For questions regarding the processing of personal data and exercising your rights under GDPR, please contact us at the email address above.`,
  },
  {
    id: 'dane',
    title: '2. Data we collect and its sources',
    content: `Depending on how you use the service, we process the following categories of data:

**2.1 Account data**
- Email address (provided during registration or login)
- Encrypted password (stored as a bcrypt hash - never in plaintext)
- Full name (optional, shown in the dashboard and team list)
- Date and time of registration, date of last login

**2.2 Client-space data (portfolio)**
The service is a tool for ISO consultants and auditors managing a portfolio of clients. When creating a space for each client, the user provides:
- Client company name
- Industry / business profile
- The chosen standard or regulation the analysis is run against

**2.3 Files uploaded for Gap Audit analysis**
The text content of DOCX/PDF/XLSX files uploaded for analysis (extracted from the file) is saved together with the analysis result - both the result and the source content are retained for the period configured in account settings (see section 4) and are not deleted immediately after the session ends. If the user enabled company-name anonymisation, the text sent for processing and to the AI provider has the company name anonymised.

**2.4 AI-proposed fixes**
When a user accepts a proposed fix (single or in batch mode), the fix text is written directly into the .docx result file - it becomes part of that same document and follows the same retention rules.

**2.5 Team data**
The account owner may invite collaborators (auditors, approvers) to work on the shared portfolio. When inviting a team member we process their email address, assigned role (Owner / Auditor / Approver) and, if applicable, a restriction to a single specific client space.

**2.6 Public report link**
A user with the Owner or Approver role may generate a secure, public link to an approved analysis result (e.g. to share it with the assessed organisation). The link contains a random access token, is valid for a period configured in account settings, and can be revoked at any time. Anyone holding the link can view the report content without logging in - the decision to generate and share the link rests solely with the user.

**2.7 Technical data and server logs**
- IP address
- Browser type and version (User-Agent)
- Date and time of HTTP request, URL called, HTTP response code

**2.8 Visit statistics**
The service counts visits to public pages (path, any UTM parameters, external referrer on first visit) for its own simple traffic statistics. The visit identifier is stored only in the browser's sessionStorage (cleared when the tab is closed) - this is not a cookie and is not used to recognise a returning visitor across visits. Details in section 6.`,
  },
  {
    id: 'cele',
    title: '3. Purposes and legal bases of processing',
    content: `| Purpose of processing | Legal basis (GDPR) |
|---|---|
| Providing service features (account, Gap Audit analysis, client portfolio management) | Art. 6(1)(b) - performance of a contract |
| Handling business inquiries and booking a product demo | Art. 6(1)(b) - steps taken at the request of the data subject prior to entering into a contract |
| Ensuring service security and detecting abuse | Art. 6(1)(f) - legitimate interest |
| Simple traffic statistics on public pages (no cookies, see section 6) | Art. 6(1)(f) - legitimate interest |
| Fulfilling legal obligations (e.g. accounting) | Art. 6(1)(c) - legal obligation |
| Pursuing or defending legal claims | Art. 6(1)(f) - legitimate interest |

**Our legitimate interest** consists of ensuring the security and stability of the service and the ability to pursue claims arising from the services provided.`,
  },
  {
    id: 'czas',
    title: '4. Data retention periods',
    content: `| Data category | Retention period |
|---|---|
| User account data | Until account deletion or 3 years from last activity |
| Gap Audit results and content of uploaded source files | **24 months** from the analysis date by default - the account owner may shorten or extend this in Settings. After the period expires, documents and related files are automatically and permanently deleted (a daily cleanup process) |
| Public report link token | Per the period configured in account settings (default 14 days) from link generation, independent of the underlying document's retention period - the link can also be revoked manually at any time |
| Invited team member data | Until removed from the team by the account owner |
| Server logs | 30 days, then automatically deleted |
| Visit identifier (sessionStorage, traffic statistics) | Until the browser tab is closed - not persisted |

Users can delete client documents at any time in the History tab of a given client space, regardless of the configured retention period. After the specified periods, data is permanently deleted in a way that prevents identification of the person or organisation.`,
  },
  {
    id: 'prawa',
    title: '5. Your rights',
    content: `Under GDPR (Regulation 2016/679) you have the following rights:

**Right of access** (Art. 15) - you may request information about what data we process, for what purpose and on what legal basis.

**Right to rectification** (Art. 16) - you may request correction of inaccurate or completion of incomplete data.

**Right to erasure** (Art. 17) - you may request deletion of your data ("right to be forgotten") in cases provided by law.

**Right to restriction of processing** (Art. 18) - you may request temporary suspension of processing your data in certain situations.

**Right to data portability** (Art. 20) - you may receive your data in a machine-readable format.

**Right to object** (Art. 21) - you may object to processing based on legitimate interest.

**Right to lodge a complaint** - you have the right to lodge a complaint with the supervisory authority: the President of the Personal Data Protection Office (UODO), ul. Stawki 2, 00-193 Warsaw, Poland, www.uodo.gov.pl.

To exercise the above rights, contact us at: kontakt@mqs.com.pl. We will respond within 30 days of receiving the request.`,
  },
  {
    id: 'cookies',
    title: '6. Cookies and local storage',
    content: `**Gap Assessment by MQS does not use cookies.** Not for login, not for tracking, and not for marketing or third-party analytics purposes (no Google Analytics, Google Tag Manager, or similar tools).

**What we store instead of cookies:**

| Name / mechanism | Technology | Purpose | Lifetime |
|---|---|---|---|
| gap_mvp_token | localStorage | Session token for the logged-in user (API request authorisation) | Until logout or manual browser data clearing |
| iso_traffic_session | sessionStorage | Identifier for a single visit, used for simple public-page traffic statistics (section 2.8) | Until the browser tab is closed |

Both technologies are **strictly necessary** for the service to work (maintaining a logged-in session) or are temporary and are not used to build a visitor profile - they do not require consent under cookie regulations, since they are not cookies and are not used for tracking.

Disabling localStorage/sessionStorage in your browser will prevent you from logging into the service.`,
  },
  {
    id: 'trzecie',
    title: '7. Recipients - third parties',
    content: `Your data may be transferred to the following categories of entities:

**7.1 IT infrastructure providers**
- **Hostinger International Ltd.** - VPS server hosting (servers in Germany / EU). Data is processed within the European Union.

**7.2 AI (Artificial Intelligence) providers**
For Gap Audit analysis and fix proposals, the system uses one of several language model providers, depending on availability - data uploaded by the user (client name, industry, content of uploaded documents) is transmitted to the currently active provider to deliver the service:
- **Mistral AI** (France, EU) - primary provider. Privacy policy: mistral.ai/terms/.
- **DeepSeek** (China) - fallback provider. Privacy policy: deepseek.com/privacy-policy.
- **Groq, Inc.** (USA) - fallback provider (inference infrastructure). Privacy policy: groq.com/privacy-policy/.

Document content sent to the AI provider is always treated strictly as data to analyse - the system applies additional software safeguards to reduce the risk that instructions hidden inside an uploaded document could influence the model's behaviour. For providers outside the EU (DeepSeek, Groq), appropriate safeguards are applied in accordance with SCCs (Standard Contractual Clauses) under Art. 46 GDPR, to the extent offered by the given provider.

**7.3 Public authorities**
The controller may be required to disclose data to public authorities (e.g. police, prosecution, courts) under applicable law.

We do not sell, rent or share personal data with third parties for marketing purposes.

**7.4 Third-party data entered by the user**
The service is a tool for ISO consultants and auditors who, as part of their work, upload data concerning their OWN clients for Gap Audit analysis - including company names and the content of their internal documents. To that extent, the user is the controller of that data, and the Service's controller (MQS Marcin Rabiasz) acts as a processor within the meaning of Art. 28 GDPR. The user is responsible for having their own legal basis for processing their clients' data. The Service offers an optional company-name anonymisation feature before data is sent to the AI provider (see section 2.3) - enabling it is at the user's discretion. A data processing agreement (Art. 28 GDPR) is available on request - contact: kontakt@mqs.com.pl.

**7.5 Recipients of the public report link**
If a user knowingly generates and shares a public link to an analysis result (section 2.6), anyone holding that link (e.g. a representative of the assessed organisation) gains access to the report content without logging in. This is a deliberate decision made by the user, not an automatic disclosure by the controller - the link can be revoked at any time.

**7.6 Payment provider**
When purchasing a Starter or Growth plan, data necessary to process the payment (including email address, transaction amount, IP address) is transferred to **PayU S.A.** (ul. Grunwaldzka 186, 60-166 Poznan, Poland, KRS 0000274399) - a national payment institution supervised by the Polish Financial Supervision Authority, acting as an independent controller of that data for payment processing purposes. The Controller does not store the user's payment card details or online banking credentials. PayU's privacy policy: payu.pl.`,
  },
  {
    id: 'bezpieczenstwo',
    title: '8. Data security',
    content: `We apply the following technical and organisational measures to protect your data:

- TLS/HTTPS connection encryption (SSL certificate)
- Passwords stored only as bcrypt hashes, constant-time login verification resistant to account-enumeration timing attacks
- Verification of each uploaded file's actual signature (magic bytes) - not just its declared extension or MIME type
- A size limit on decompressed content of uploaded .docx/.xlsx files, protecting against excessive server memory use
- Server access restricted to SSH keys
- Data isolation between accounts and client spaces, enforced server-side on every request
- Regular data backups, principle of minimum privileges for data access

Despite the measures in place, no internet data transmission is 100% secure. In the event of a personal data breach, we will notify users and the supervisory authority (UODO) in accordance with GDPR requirements (Art. 33-34).`,
  },
  {
    id: 'zmiany',
    title: '9. Changes to this Privacy Policy',
    content: `The controller reserves the right to make changes to this Privacy Policy.

Users with accounts in the service will be informed of significant changes by email, and all visitors will be notified via a notice on the homepage.

Date of last update: **${UPDATED}**

The current version of the Privacy Policy is always available at: gapassessment.pl/polityka-prywatnosci`,
  },
  {
    id: 'kontakt',
    title: '10. Contact',
    content: `For questions regarding personal data protection, please contact:

**Email:** kontakt@mqs.com.pl
**Website:** gapassessment.pl

**Supervisory authority:**
President of the Personal Data Protection Office (UODO)
ul. Stawki 2, 00-193 Warsaw, Poland
Tel.: +48 606-950-000
www.uodo.gov.pl`,
  },
];

function renderContent(text) {
  const lines = text.split('\n');
  const result = [];
  let tableRows = [];
  let inTable = false;

  const flushTable = () => {
    if (!tableRows.length) return;
    const headers = tableRows[0].split('|').slice(1, -1).map(c => c.trim());
    const rows = tableRows.slice(2);
    result.push(
      <div key={`tbl-${result.length}`} style={{ overflowX: 'auto', margin: '12px 0 16px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
          <thead>
            <tr>
              {headers.map((h, i) => (
                <th key={i} style={{ padding: '8px 12px', textAlign: 'left', background: 'var(--line-soft)', color: 'var(--ink-soft)', fontWeight: 600, borderBottom: '1px solid var(--line-soft)', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => {
              const cells = row.split('|').slice(1, -1).map(c => c.trim());
              return (
                <tr key={ri} style={{ borderBottom: '1px solid var(--panel2)' }}>
                  {cells.map((c, ci) => (
                    <td key={ci} style={{ padding: '8px 12px', color: 'var(--ink-soft)', verticalAlign: 'top' }}>{renderInline(c)}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
    tableRows = [];
    inTable = false;
  };

  lines.forEach((line, i) => {
    if (line.startsWith('|')) {
      inTable = true;
      tableRows.push(line);
      return;
    }
    if (inTable) flushTable();

    if (!line.trim()) {
      result.push(<div key={i} style={{ height: 8 }} />);
    } else if (line.startsWith('**') && line.endsWith('**') && line.length > 4) {
      result.push(<p key={i} style={{ fontWeight: 700, color: 'var(--ink)', margin: '14px 0 6px', fontSize: '0.9rem' }}>{line.slice(2, -2)}</p>);
    } else {
      result.push(<p key={i} style={{ color: 'var(--ink-soft)', margin: '0 0 8px', lineHeight: 1.7, fontSize: '0.88rem' }}>{renderInline(line)}</p>);
    }
  });

  if (inTable) flushTable();
  return result;
}

function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    /^\*\*[^*]+\*\*$/.test(p)
      ? <strong key={i} style={{ color: 'var(--ink)', fontWeight: 700 }}>{p.slice(2, -2)}</strong>
      : p
  );
}

export default function PrivacyPage() {
  const navigate = useNavigate();
  const { lang } = useLang();

  const sections = lang === 'en' ? SECTIONS_EN : SECTIONS;

  return (
    <div className="legalp" style={{ minHeight: '100vh', background: 'var(--paper)', color: 'var(--ink)', fontFamily: "'Inter','Inter Fallback','Segoe UI',sans-serif" }}>
      <style>{LEGAL_TOKENS}</style>
      <nav style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: 'var(--paper-raised)', backdropFilter: 'blur(12px)',
        borderBottom: '1px solid var(--line-soft)',
        padding: '0 max(1.5rem, calc((100vw - 1100px)/2))',
        display: 'flex', alignItems: 'center', gap: 16, height: 60,
      }}>
        <a href="/" onClick={ev => { ev.preventDefault(); navigate('/'); }} style={{ background: 'none', border: 'none', color: 'var(--ink-soft)', cursor: 'pointer', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 6, padding: 0, textDecoration: 'none' }}>
          {lang === 'en' ? '← Home' : '← Strona główna'}
        </a>
        <span style={{ color: 'var(--line-soft)' }}>|</span>
        <span style={{ fontSize: '1rem', fontWeight: 800, color: 'var(--ink)' }}>Gap Assessment <span style={{ color: 'var(--accent)' }}>by MQS</span></span>
        <PublicToggleBar style={{ marginLeft: 'auto' }} />
      </nav>

      <main style={{ padding: '48px 1.5rem 80px', maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--accent)', letterSpacing: 2, textTransform: 'uppercase' }}>RODO / GDPR</span>
        </div>
        <h1 style={{ fontSize: 'clamp(1.6rem, 3vw, 2.4rem)', fontWeight: 800, color: 'var(--ink)', marginBottom: 10, lineHeight: 1.1 }}>
          {lang === 'en' ? 'Privacy Policy' : 'Polityka Prywatności'}
        </h1>
        <p style={{ color: 'var(--ink-soft)', fontSize: '0.85rem', marginBottom: 48 }}>
          {lang === 'en' ? `Last updated: ${UPDATED} - Version 1.0` : `Ostatnia aktualizacja: ${UPDATED} - Wersja 1.0`}
        </p>

        <style>{`
          .pp-layout { display: grid; grid-template-columns: 240px 1fr; gap: 48px; align-items: start; }
          .pp-nav { position: sticky; display: block; }
          @media (max-width: 767px) {
            .pp-layout { grid-template-columns: minmax(0, 1fr) !important; gap: 0 !important; }
            .pp-nav { display: none !important; }
          }
        `}</style>
        <div className="pp-layout">
          <nav className="pp-nav" style={{ top: 80 }}>
            <p style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--line)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 12 }}>{lang === 'en' ? 'Table of contents' : 'Spis treści'}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {sections.map(s => (
                <a key={s.id} href={`#${s.id}`} style={{ fontSize: '0.8rem', color: 'var(--ink-soft)', textDecoration: 'none', padding: '5px 10px', borderRadius: 6, transition: 'all 0.15s', display: 'block', lineHeight: 1.4 }}
                  onMouseEnter={ev => { ev.target.style.color = 'var(--ink)'; ev.target.style.background = 'var(--line-soft)'; }}
                  onMouseLeave={ev => { ev.target.style.color = 'var(--ink-soft)'; ev.target.style.background = 'transparent'; }}>
                  {s.title}
                </a>
              ))}
            </div>
            <div style={{ marginTop: 28, padding: '16px', background: 'var(--accent-soft)', border: '1px solid var(--accent)', borderRadius: 10 }}>
              <p style={{ fontSize: '0.75rem', color: 'var(--accent)', fontWeight: 700, marginBottom: 6 }}>{lang === 'en' ? 'Data contact' : 'Kontakt ws. danych'}</p>
              <p style={{ fontSize: '0.75rem', color: 'var(--ink-soft)', margin: 0, lineHeight: 1.5 }}>kontakt@mqs.com.pl</p>
            </div>
          </nav>

          <div>
            <div style={{ background: 'var(--accent-soft)', border: '1px solid var(--accent)', borderRadius: 12, padding: '16px 20px', marginBottom: 36 }}>
              <p style={{ fontSize: '0.85rem', color: 'var(--accent)', fontWeight: 600, marginBottom: 4 }}>{lang === 'en' ? 'Summary' : 'Krótkie podsumowanie'}</p>
              <p style={{ fontSize: '0.82rem', color: 'var(--ink-soft)', lineHeight: 1.65, margin: 0 }}>
                {lang === 'en'
                  ? 'Gap Assessment by MQS collects the minimum data needed to provide the service: your account email and password, client-portfolio data you enter, and server logs. We use no cookies and no third-party analytics - only a strictly necessary login token in your browser’s local storage. We do not sell data. Server is hosted in the EU (Germany). You can delete your data at any time by emailing kontakt@mqs.com.pl.'
                  : 'Gap Assessment by MQS zbiera minimum danych niezbędnych do świadczenia usług: adres e-mail i hasło do konta, dane portfela klientów które wprowadzasz, oraz logi serwera. Nie używamy żadnych cookies ani analityki trzeciej strony - jedynie ściśle niezbędny token logowania w lokalnym przechowywaniu przeglądarki. Nie sprzedajemy danych. Serwer hostowany jest w UE (Niemcy). Dane możesz usunąć w dowolnym momencie - wystarczy wiadomość na kontakt@mqs.com.pl.'}
              </p>
            </div>

            {sections.map(s => (
              <section key={s.id} id={s.id} style={{ marginBottom: 48, scrollMarginTop: 80 }}>
                <h2 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--ink)', marginBottom: 16, paddingBottom: 10, borderBottom: '1px solid var(--line-soft)' }}>{s.title}</h2>
                <div>{renderContent(s.content)}</div>
              </section>
            ))}
          </div>
        </div>
      </main>

      <footer style={{ borderTop: '1px solid var(--line-soft)', padding: '20px max(1.5rem, calc((100vw - 1100px)/2))', display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--ink)' }}>Gap Assessment <span style={{ color: 'var(--accent)' }}>by MQS</span></span>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <a href="/polityka-prywatnosci" style={{ fontSize: '0.78rem', color: 'var(--accent)', textDecoration: 'none' }}>{lang === 'en' ? 'Privacy Policy' : 'Polityka prywatnosci'}</a>
          <a href="/regulamin" style={{ fontSize: '0.78rem', color: 'var(--ink-soft)', textDecoration: 'none' }}>{lang === 'en' ? 'Terms of Service' : 'Regulamin'}</a>
          <a href="/" style={{ fontSize: '0.78rem', color: 'var(--ink-soft)', textDecoration: 'none' }}>{lang === 'en' ? 'Home' : 'Strona glowna'}</a>
        </div>
      </footer>
      <PrerenderReady />
    </div>
  );
}
